<template>
  <div @click.stop>
    <BittsModal
      :model-value="visible"
      variant="data"
      :width="480"
      :use-mask-to-close="true"
      :action-loading="saving"
      :disabled="isNameEmptyOrInvalid"
      title="Convert to Custom Population"
      description="This population needs a new custom name. This name change will be reflected in all reports."
      @closed="onClose"
      @action="onConvert"
    >
      <div class="py-24">
        <BittsInput
          v-model="newPopulationName"
          placeholder="Rename Population"
          form-label="New Population Name"
          name="rename-population-input"
          class="mb-8"
          :status="isNameInvalid ? 'danger' : 'default'"
          :danger-text="errorText"
          @click.stop
        />
      </div>
    </BittsModal>
  </div>
</template>

<script>
import { BittsInput, BittsModal } from '@crossbeam/bitts';

import { mapActions, mapState } from 'pinia';

import { crossbeamApi } from '@/api';
import { STANDARD_POPULATION_NAMES } from '@/constants/standard_populations';
import { useFlashesStore, usePopulationsStore } from '@/stores';

export default {
  name: 'ConvertToCustomModal',
  components: {
    BittsInput,
    BittsModal,
  },
  props: {
    hasPopulationRefresh: {
      type: Boolean,
      default: false,
    },
    population: {
      type: Object,
      required: false,
      default: () => {
        // do nothing
      },
    },
    standardPopulation: {
      type: Object,
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['closed', 'population-converted'],
  data() {
    return {
      saving: false,
      newPopulationName: '',
    };
  },
  computed: {
    ...mapState(usePopulationsStore, ['getPopulationByName']),
    errorText() {
      if (this.isNameStandard) {
        return 'Population name cannot be the same as any standard Population name.';
      }
      return 'Population names must be unique.';
    },
    isNameAlreadyInUse() {
      return !!this.getPopulationByName(this.newPopulationName);
    },
    isNameEmptyOrInvalid() {
      return !this.newPopulationName || this.isNameInvalid;
    },
    isNameInvalid() {
      return !this.saving && (this.isNameAlreadyInUse || this.isNameStandard);
    },
    isNameStandard() {
      return STANDARD_POPULATION_NAMES.includes(this.newPopulationName);
    },
  },
  methods: {
    ...mapActions(useFlashesStore, ['addSuccessFlash', 'addErrorFlash']),
    onClose() {
      this.$emit('closed');
      this.newPopulationName = '';
    },
    async onConvert() {
      try {
        this.saving = true;
        const payload = {
          name: this.newPopulationName,
          standard_type: null,
          segment: this.standardPopulation.segment,
        };
        if (this.hasPopulationRefresh) {
          payload.description = this.population.description || null;
        }
        const { error } = await crossbeamApi.PATCH('/v0.3/populations/{id}', {
          params: {
            path: {
              id: this.standardPopulation.id,
            },
          },
          body: payload,
        });
        if (error) throw error;
        this.$emit('population-converted');
        this.addSuccessFlash({ message: 'Population Converted' });
      } catch (_error) {
        this.addErrorFlash({
          message: 'Population Not Saved',
          description:
            'Please try again or contact support@crossbeam.com for help.',
        });
      }
    },
  },
};
</script>
